// pages/roles.js — Roles page (top-level nav, PR #130).
//
// Owns the `data-page="roles"` section. Renders the per-tenant
// role list + custom-role builder. Cloud-only — solo mode shows
// a banner pointing the user at the Team-connection setting.
//
// Until PR #130 the Roles panel lived inside the Team page and
// was rendered as a side-effect of renderTeamPageInner. Moving it
// to a top-level nav item means rendering it must own its own
// lifecycle: initial mount on first .active, periodic re-render
// on dashboard:tick while active, no-op when hidden.
//
// canManage source: reads `teamState.info.your_role` /
// `your_team_role` directly (same gate team-page.js used). The
// Roles page never paints until teamState.info is populated; the
// initial paint will be empty if the user lands on /roles before
// the first /v1/teams/me round-trip lands, then the next tick fills
// it in.

import { state } from "../state.js";
import { teamState } from "../page-state.js";
import { $ } from "../dom.js";
import { renderRolesPanel, wireRoleEditor } from "../team-roles.js";
import { startCheckout } from "../billing.js";

// Track whether wireRoleEditor has been called this page-load.
// The wire step attaches click + keydown handlers on the global
// modal backdrop; repeating it would stack duplicate listeners.
// (wireRoleEditor itself is internally idempotent via its own
// `editorWired` flag, but reading the gate here is cheap insurance
// and documents the contract at the call site.)
let editorWired = false;

// `latestCanManage` lets wireRoleEditor's getter pick up the
// current authz value at click time rather than capturing whatever
// was true at first-render. Mirrors the pattern team-page.js used
// when the panel still lived there — if the user's role flips
// mid-session, the next click on "+ New role" or Save reads the
// fresh value.
let latestCanManage = false;

function computeCanManage() {
  const info = teamState.info;
  if (!info) return false;
  // Owner role retired in migration 035 — any admin can now manage
  // roles. your_team_role === "owner" is preserved on the second
  // clause for back-compat with JWTs minted pre-migration.
  return info.your_role === "admin"
    || info.your_team_role === "admin"
    || info.your_team_role === "owner";
}

export function renderRolesPage() {
  const section = document.querySelector('section.page[data-page="roles"]');
  if (!section) return;

  const notConnected = $("roles-not-connected");
  const soloEmpty = $("roles-solo-empty");
  const panel = $("team-roles-panel");
  if (!teamState.info) {
    // Solo mode or pre-/v1/teams/me. Show the banner pointing the
    // user at Team-connection setup; hide the panel + solo-empty
    // until cloud info arrives.
    if (notConnected) notConnected.style.display = "";
    if (soloEmpty) soloEmpty.style.display = "none";
    if (panel) panel.style.display = "none";
    return;
  }
  if (notConnected) notConnected.style.display = "none";

  // Solo Free-tier empty state. Three conjunct conditions:
  //   1. tier === "solo" — paying customers see the full Roles UI
  //      even if they have 1 user (they may want to pre-configure
  //      roles before inviting).
  //   2. user_count <= 1 — distinct humans, not active laptop tokens.
  //      A solo user with 2 laptops still has 1 user and no one to
  //      assign roles to. Sweep caught this footgun.
  //   3. (rendered for admins only — non-admins can't act on the
  //      upgrade CTA, so we suppress the nudge for them. They'll
  //      see the full Roles list with whatever they have scopes
  //      for; in practice non-admins on a solo tenant don't exist
  //      since the tenant creator is the lone admin.)
  // Fail-open on missing user_count: typeof check returns 999 →
  // not solo → show full Roles UI.
  const info = teamState.info;
  const userCount = typeof info.user_count === "number" ? info.user_count : 999;
  const isFreeTier = info.tier === "solo";
  const isSoloUser = userCount <= 1;
  const isAdmin = info.your_team_role === "admin"
    || info.your_role === "admin"
    || info.your_team_role === "owner";  // back-compat for pre-035 JWTs
  if (isFreeTier && isSoloUser && isAdmin) {
    if (soloEmpty) soloEmpty.style.display = "";
    if (panel) panel.style.display = "none";
    // Wire the upgrade button once. The button calls startCheckout
    // (same flow Account → Billing uses) → Stripe Checkout.
    //
    // startCheckout in billing.js swallows its own errors via an
    // internal try/catch + showToast — it does NOT re-throw. So our
    // outer try/catch would never fire on failure, and the button
    // would stay permanently disabled at "Loading checkout…".
    // Use .finally() to guarantee the button gets restored regardless
    // of outcome. On success: location.href flips and the page
    // navigates away so the button state is moot.
    const upgradeBtn = $("roles-solo-upgrade-btn");
    if (upgradeBtn && !upgradeBtn.dataset.wired) {
      upgradeBtn.dataset.wired = "1";
      upgradeBtn.addEventListener("click", async () => {
        upgradeBtn.disabled = true;
        const prev = upgradeBtn.textContent;
        upgradeBtn.textContent = "Loading checkout…";
        try {
          await startCheckout("startup");
        } finally {
          // Restore unless we're already navigating away.
          if (!document.hidden) {
            upgradeBtn.disabled = false;
            upgradeBtn.textContent = prev;
          }
        }
      });
    }
    return;
  }
  if (soloEmpty) soloEmpty.style.display = "none";

  latestCanManage = computeCanManage();
  renderRolesPanel(latestCanManage);
  if (!editorWired) {
    wireRoleEditor(() => latestCanManage);
    editorWired = true;
  }
}

// ---- mount ----

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  // Initial paint. Roles fetches its own data via renderRolesPanel
  // (which has its own 5s cache), so no fetchers.js prefetch needed.
  renderRolesPage();
  tickHandler = () => {
    if (state.activePage === "roles") renderRolesPage();
  };
  document.addEventListener("dashboard:tick", tickHandler);
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="roles"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
